import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useStringFlagValue } from '@openfeature/react-sdk'
import { light, solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { track } from '@amplitude/analytics-browser'

import {
  StripeLineItem,
  fetchCheckoutSession,
} from '@/actions/settings/billingActions'
import { getCurrentUser } from '@/selectors/user.selectors'
import {
  fetchAdvisoryPrices,
  PROD_ADVISORY_PRICING,
} from '@/constants/pricingConstants'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { FEATURE_FLAG_KEYS } from '@/features/open-feature'

import { Button, Card, Text, Badge, Icon, Image, CardColors } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'

import { selectFinancialAdvisoryProfile } from '@/features/financial-advisory/financialAdvisory.selectors'
import { userHasFinancialAdvisoryAccess } from '@/features/financial-advisory/service'
import {
  FinancialAdvisoryFeatureFlagValues,
  FinancialAdvisoryProfilesStatus,
} from '@/features/financial-advisory/financialAdvisory.slice'

const AdvisoryLaunchCard = () => {
  const navigate = useNavigate()
  const [isDismissed, setIsDismissed] = useState(false)
  const currentUser = useReselector(getCurrentUser)
  const ffValue = useStringFlagValue(
    FEATURE_FLAG_KEYS.financialAdvisoryPilot,
    FinancialAdvisoryFeatureFlagValues.noPilot
  )
  const handleLearnMoreButtonClick = () => {
    track('clicked financial advisory phase 2 dashboard card button', {
      user_id: currentUser?.id,
      //  Tracks user phase
      user_phase: ffValue,
    })
    navigate('/advisory/overview')
  }
  if (isDismissed) {
    return null
  }
  // Only show Dashboard if user has financialAdvisoryProfile (means they have access) and user has not paid
  return (
    <Card color={CardColors.EMPHASIS} className="p-6 space-y-4">
      <div className="flex justify-between items-center">
        <Badge color="orange" variant="secondary">
          <Icon icon={solid('sparkle')} className="mr-2" />
          New
        </Badge>
        <Icon
          icon={solid('times')}
          className="cursor-pointer"
          onClick={() => setIsDismissed(true)}
        />
      </div>

      <Text variant={TypographyStyles.H2}>Introducing: Financial Advisory</Text>

      <Image
        src="https://heard-images.s3.amazonaws.com/assets/financial_advice.svg"
        alt="financial advisory"
        className="h-[150px]"
      />
      <Text variant={TypographyStyles.BODY_MD} className="mb-4 block">
        <br />
        Get expert, personalized support to help you achieve both your personal
        and business financial goals—all for just an additional $
        {PROD_ADVISORY_PRICING.monthly.amount} per month.
      </Text>
      <Text variant={TypographyStyles.BODY_MD} className="mb-4 block">
        <br />
        Schedule your free 20 minute Introductory Advisory Call with a Certified
        Financial Planner (CFP®) to learn more.
      </Text>
      <Button variant="secondary" onClick={handleLearnMoreButtonClick}>
        Learn More
        <Icon icon={light('arrow-right')} className="ml-4" />
      </Button>
    </Card>
  )
}

const AdvisoryPurchaseCard = () => {
  const dispatch = useAppDispatch()
  const [loading, setLoading] = useState(false)
  const currentUser = useReselector(getCurrentUser)
  const advisoryPrices = fetchAdvisoryPrices()
  const annualPlan = advisoryPrices.annual.priceId
  const monthlyPlan = advisoryPrices.monthly.priceId

  /**
   * Navs to Payment
   */
  const navToPayment = useCallback(
    async (selectedPriceId: string) => {
      setLoading(true)
      if (!currentUser?.id || !selectedPriceId) {
        return
      }

      track('click nav to advisory purchase — dashboard card ', {
        user_id: currentUser.id,
      })

      const lineItems: StripeLineItem[] = [
        {
          priceId: selectedPriceId,
          quantity: 1,
        },
      ]
      const metadata: Record<string, string> = {}

      let subscriptionCheckoutUrl: string | null = null
      const res = await fetchCheckoutSession({
        userId: currentUser.id,
        lineItems,
        success_url: {
          url: '/advisory/overview?paymentComplete=true',
        },
        // Navigate back to current page with query params
        cancel_url: {
          url: `${location.pathname}${location.search}`,
        },
        freeTrialEnabled: undefined,
        freeTrialSearchParamValue: null,
        metadata,
      })(dispatch)
      if (res) {
        subscriptionCheckoutUrl = res.url
      }

      if (subscriptionCheckoutUrl !== null) {
        setLoading(false)

        window.location.href = subscriptionCheckoutUrl
      }
    },
    [currentUser?.id, dispatch]
  )

  return (
    <Card
      color={CardColors.LEVEL_1}
      className="mobile-squared-corners bg-moss p-6 space-y-4"
    >
      <div>
        <Badge variant="secondary" color="green">
          Almost Complete
        </Badge>
        <Text variant={TypographyStyles.H2} className="mt-2">
          Add On Financial Advisory
        </Text>
      </div>

      <div className="flex justify-center items-center">
        <Image
          src="https://heard-images.s3.amazonaws.com/assets/advisory_screen.svg"
          alt="financial advisory"
          className="w-full"
        />
      </div>

      <Text variant={TypographyStyles.BODY_MD}>
        We hoped you enjoyed your free introductory call with a Certified
        Financial Planner! To unlock expert financial guidance for your private
        practice, add the Advisory add on and receive your 100% personalized,
        comprehensive financial plan.
      </Text>

      <div className="text-center space-y-4">
        <Button
          variant="primary"
          loading={loading}
          disabled={loading}
          onClick={() => navToPayment(annualPlan)}
        >
          Get Started with Annual (Save 15%)
        </Button>

        <Button
          variant="secondary"
          loading={loading}
          disabled={loading}
          onClick={() => navToPayment(monthlyPlan)}
        >
          Get Started with Monthly
        </Button>

        <Text variant={TypographyStyles.BODY_MD} color={TextColors.MUTED}>
          $29 per month, or $300 per year
        </Text>
      </div>
    </Card>
  )
}

export const AdvisoryDashboardCard = () => {
  const faProfile = useReselector(selectFinancialAdvisoryProfile)

  const hasAdvisoryAccess = userHasFinancialAdvisoryAccess()
  // Only show Dashboard if user has financialAdvisoryProfile (means they have access) and user has not paid
  /**
   * 1. CTA to Try Advisory
   *
   * This shows when users have Access to fianncial advisory, but have not scheduled a call or tried it out
   */
  if (
    hasAdvisoryAccess &&
    faProfile?.status === FinancialAdvisoryProfilesStatus.profile_generated
  ) {
    return <AdvisoryLaunchCard />
  } else if (
    /**
     * 2. Post Introductory 20 Minute Call
     *
     * This shows when users have Access to financial advisory, and have attended their intro call, but have not purchased Advisory
     */
    hasAdvisoryAccess &&
    faProfile?.status ===
      FinancialAdvisoryProfilesStatus.intro_call_completed &&
    faProfile?.subscriptionStatus !== 'paid'
  ) {
    return <AdvisoryPurchaseCard />
    /**
     * Otherwise, do not show card
     */
  } else {
    return null
  }
}
